// ./src/components/features/Submissions/SubmissionsTable/SubmissionsTable.tsx
import React, { useMemo } from "react";
import { Text, Badge, Group, Stack, Tooltip } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  AppButton,
  AppIcon,
} from "@/components/generic";
import { Submission, FormField, Form } from "@/utils/api";
import { formatDateTime, parseUserAgent } from "@/utils";
import { SubmissionStatusBadge } from "../SubmissionStatusBadge";
import { FIELD_TYPE_IDS, OPERATION_RIGHTS } from "@/config";

export interface SubmissionsTableProps {
  /** Array of submissions to display */
  submissions: Submission[];

  form?: Form | null | undefined;

  /** Form field definitions for displaying proper labels */
  formFields?: FormField[] | undefined;

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when view details action is clicked */
  onViewSubmission?: (submission: Submission) => void;

  /** Callback when remove action is clicked */
  onRemoveSubmission?: (submission: Submission) => void;

  /** Callback when a row is clicked */
  onRowClick?: (submission: Submission) => void;

  /** ID of submission currently being removed */
  removingSubmissionId?: string | null;

  /** ID of submission just viewed or added */
  highlightedSubmissionId?: string | null | undefined;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

const DO_NOT_SHOW_FEILD_TYPES = [
  FIELD_TYPE_IDS.RECAPTCHA2,
  FIELD_TYPE_IDS.RECAPTCHA3,
  FIELD_TYPE_IDS.RECAPTCHA_ENTERPRISE,
  FIELD_TYPE_IDS.ANTISPAMBOT,
  FIELD_TYPE_IDS.FILE,
];

/**
 * Labels for spam reason codes
 */
const SPAM_REASON_LABELS: Record<string, string> = {
  gibberish: "Gibberish",
  external_api: "External API",
  local_model: "Local Model",
  honeypot: "Honeypot",
  ai_spam: "AI Spam",
  ip_filter: "IP Blocked",
};

/**
 * Parse a single spam reason and return display info
 */
interface ParsedSpamReason {
  label: string;
  details?: string;
}

const parseSpamReason = (reason: string): ParsedSpamReason => {
  // Check for detailed gibberish format: gibberish:fieldLabel:level:source:score
  if (reason.startsWith("gibberish:")) {
    const parts = reason.split(":");
    if (parts.length >= 5) {
      const [, fieldLabel, level, source, scoreStr] = parts;
      return {
        label: `Gibberish: ${fieldLabel}`,
        details: `${level} (${source}), score: ${scoreStr}%`,
      };
    }
  }

  // Check for AI spam format: ai_spam:score:explanation
  if (reason.startsWith("ai_spam:")) {
    const parts = reason.split(":");
    if (parts.length >= 3) {
      const [, scoreStr, ...explanationParts] = parts;
      const explanation = explanationParts.join(":"); // Rejoin in case explanation has colons
      return {
        label: `AI Spam (${scoreStr}%)`,
        details: explanation || "Flagged by AI analysis",
      };
    }
  }

  // Check for IP filter format: ip_filter:mode:clientIp
  if (reason.startsWith("ip_filter:")) {
    const parts = reason.split(":");
    if (parts.length >= 3) {
      const [, mode, clientIp] = parts;
      return {
        label: mode === "allow" ? "IP Not Allowed" : "IP Denied",
        details: `IP: ${clientIp}`,
      };
    }
  }

  // Simple reason format
  return {
    label: SPAM_REASON_LABELS[reason] || reason,
  };
};

/**
 * Parse and format spam reasons for display
 */
const formatSpamReasons = (
  spamReason: string | undefined
): ParsedSpamReason[] => {
  if (!spamReason) return [];
  return spamReason
    .split(",")
    .map((r) => r.trim())
    .filter((r) => r.length > 0)
    .map(parseSpamReason);
};

export const SubmissionsTable: React.FC<SubmissionsTableProps> = ({
  submissions,
  form,
  formFields,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onViewSubmission,
  onRemoveSubmission,
  onRowClick,
  removingSubmissionId,
  highlightedSubmissionId,
  size = "md",
}) => {
  const canRemove = form?.rights?.includes(OPERATION_RIGHTS.REMOVE);

  // Parse formData from all submissions to extract dynamic columns
  const dynamicColumns = useMemo(() => {
    // If form fields are provided, use their order
    if (formFields && formFields.length > 0) {
      return formFields
        .filter(
          (field) =>
            !DO_NOT_SHOW_FEILD_TYPES.includes(
              field.fieldType ?? FIELD_TYPE_IDS.SHORT_TEXT
            )
        )
        .map((field) => field.name);
    }

    // No form fields provided - extract from formData in order they appear
    const orderedKeys: string[] = [];
    const seenKeys = new Set<string>();

    submissions.forEach((submission) => {
      if (submission.formData) {
        try {
          const parsed = JSON.parse(submission.formData);
          // Iterate over keys in their natural order
          Object.keys(parsed).forEach((key) => {
            if (!seenKeys.has(key)) {
              orderedKeys.push(key);
              seenKeys.add(key);
            }
          });
        } catch (error) {
          console.error("Failed to parse formData:", error);
        }
      }
    });

    return orderedKeys;
  }, [submissions, formFields]);

  // Helper to get field label from form field definitions
  const getFieldLabel = (fieldName: string | undefined): string => {
    const field = formFields?.find((f) => f.name === fieldName);
    return field?.label ?? fieldName ?? "--";
  };

  // Helper to get parsed form data value
  const getFormDataValue = (
    submission: Submission,
    key: string | undefined
  ): React.ReactNode => {
    if (!submission.formData) return <Text c="dimmed">—</Text>;

    try {
      const parsed = JSON.parse(submission.formData);
      const value = key ? parsed[key] : "--";

      if (value === null || value === undefined || value === "") {
        return <Text c="dimmed">—</Text>;
      }

      // Handle different value types
      if (typeof value === "boolean") {
        return (
          <Badge variant="light" color={value ? "green" : "gray"} size="sm">
            {value ? "Yes" : "No"}
          </Badge>
        );
      }

      if (Array.isArray(value)) {
        return <Text size="sm">{value.join(", ")}</Text>;
      }

      if (typeof value === "object") {
        return <Text size="sm">{JSON.stringify(value)}</Text>;
      }

      // Truncate long text values
      const stringValue = String(value);
      if (stringValue.length > 50) {
        return (
          <Text size="sm" lineClamp={4} title={stringValue}>
            {stringValue}
          </Text>
        );
      }

      return <Text size="sm">{stringValue}</Text>;
    } catch (error) {
      console.error("Failed to parse formData:", error);
      return <Text c="dimmed">—</Text>;
    }
  };

  // Define submission date column
  const submissionDateColumn: AppTableColumn<Submission> = useMemo(
    () => ({
      key: "submissionDttm",
      label: "Submitted",
      sortable: true,
      width: 150,
      minWidth: 150,
      render: (value) => {
        if (!value) return <Text c="dimmed">—</Text>;
        return (
          <Text size="sm" fw={500}>
            <div
              dangerouslySetInnerHTML={{
                __html: formatDateTime(value, "datetime2"),
              }}
            />
          </Text>
        );
      },
    }),
    []
  );

  // Define dynamic columns from formData
  const formDataColumns: AppTableColumn<Submission>[] = useMemo(
    () =>
      dynamicColumns.map((key) => ({
        key: `formData.${key}`,
        label: getFieldLabel(key),
        sortable: false,
        width: 200,
        minWidth: 150,
        render: (_, record) => getFormDataValue(record, key),
      })),
    [dynamicColumns, formFields]
  );

  // Define browser/device + IP column (combined)
  const browserIpColumn: AppTableColumn<Submission> = useMemo(
    () => ({
      key: "browserIp",
      label: "Browser / IP",
      sortable: false,
      width: 220,
      minWidth: 200,
      render: (_, record) => {
        const { browser, os } = parseUserAgent(record.userAgent);
        const browserInfo = browser !== "Unknown" ? browser : os;
        const ip = record.remoteIp || "—";

        return (
          <Stack gap={2}>
            <Text size="sm" lineClamp={1} title={`${browser} on ${os}`}>
              {browserInfo}
            </Text>
            <Text size="xs" c="dimmed">
              {ip}
            </Text>
          </Stack>
        );
      },
    }),
    []
  );

  // Define status column with spam indicator
  const statusColumn: AppTableColumn<Submission> = useMemo(
    () => ({
      key: "status",
      label: "Status",
      sortable: true,
      width: 140,
      minWidth: 140,
      render: (value, record) => {
        const hasSpamInfo = record.spamReason;
        const spamReasons = formatSpamReasons(record.spamReason);

        // Build tooltip content for spam info
        const tooltipContent = hasSpamInfo ? (
          <Stack gap={4}>
            {spamReasons.length > 0 && (
              <Stack gap={2}>
                {spamReasons.map((reason, idx) => (
                  <Text key={idx} size="xs">
                    {reason.label}
                    {reason.details && (
                      <Text span size="xs" c="dimmed">
                        {" "}
                        — {reason.details}
                      </Text>
                    )}
                  </Text>
                ))}
              </Stack>
            )}
            {record.gibberishScore !== undefined &&
              record.gibberishScore > 0 &&
              !record.spamReason?.includes("gibberish:") && (
                <Text size="xs">Gibberish score: {record.gibberishScore}%</Text>
              )}
            {record.spamScore !== undefined &&
              record.spamScore > 0 &&
              !record.spamReason?.includes("ai_spam:") && (
                <Text size="xs">Spam score: {record.spamScore}%</Text>
              )}
            {record.spamEngine && (
              <Text size="xs" c="dimmed">
                Engine:{" "}
                {record.spamEngine.startsWith("openai-")
                  ? `OpenAI ${record.spamEngine.replace("openai-", "")}`
                  : record.spamEngine.startsWith("claude-")
                  ? `Claude ${record.spamEngine.replace("claude-", "")}`
                  : record.spamEngine}
              </Text>
            )}
          </Stack>
        ) : null;

        return (
          <Group gap={6} wrap="nowrap">
            <SubmissionStatusBadge status={value} />
            {hasSpamInfo && (
              <Tooltip
                label={tooltipContent}
                position="top"
                withArrow
                multiline
                w={250}
              >
                <span style={{ display: "flex", alignItems: "center" }}>
                  <AppIcon
                    iconName="ShieldExclamation"
                    size={16}
                    color="orange"
                  />
                </span>
              </Tooltip>
            )}
          </Group>
        );
      },
    }),
    []
  );

  // Define files column
  const filesColumn: AppTableColumn<Submission> = useMemo(
    () => ({
      key: "filesCnt",
      label: "Files",
      sortable: true,
      width: 100,
      minWidth: 100,
      align: "center",
      render: (value) => {
        const count = value || 0;
        if (count === 0) {
          return <Text c="dimmed">—</Text>;
        }
        return (
          <Badge variant="light" color="blue" size="sm">
            {count}
          </Badge>
        );
      },
    }),
    []
  );

  // Define actions column
  const actionsColumn: AppTableColumn<Submission> = useMemo(
    () => ({
      key: "actions",
      label: "",
      width: 120,
      minWidth: 120,
      align: "left",
      render: (_, record: Submission) => {
        const recordRemoveRights =
          record.rights && record.rights?.length > 0
            ? record.rights?.includes(OPERATION_RIGHTS.REMOVE)
            : canRemove;
        return (
          <Group gap="xs" wrap="nowrap" justify="flex-end">
            <AppButton
              variant="secondary"
              size="xs"
              iconName="Eye"
              onClick={() => onViewSubmission?.(record)}
              disabled={removingSubmissionId === record.id}
              tooltip="View the ubmission details"
            ></AppButton>
            <AppButton
              variant="danger"
              size="xs"
              iconName="Trash"
              onClick={() => onRemoveSubmission?.(record)}
              disabled={
                removingSubmissionId === record.id || !recordRemoveRights
              }
              inProgress={removingSubmissionId === record.id}
              tooltip="Remove the submission"
            ></AppButton>
          </Group>
        );
      },
    }),
    [onViewSubmission, onRemoveSubmission, removingSubmissionId]
  );

  // Combine all columns in order: date, form data, browser/IP, status, files, actions
  const allColumns = useMemo(
    () => [
      submissionDateColumn,
      ...formDataColumns,
      browserIpColumn,
      statusColumn,
      filesColumn,
      actionsColumn,
    ],
    [
      submissionDateColumn,
      formDataColumns,
      browserIpColumn,
      statusColumn,
      filesColumn,
      actionsColumn,
    ]
  );

  return (
    <AppTable
      data={submissions}
      hideOnNoData={true}
      columns={allColumns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(submission) => submission?.id || ""}
      highlightRow={(submission) => submission?.id === highlightedSubmissionId}
      onRowClick={onRowClick}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
